import { getPatientProductsEndpointWithQueryParameters } from "utils/patient-products.util";
import { viteEnv } from "utils/vite-env.util";

import {
  formatValidQueryParamUrl,
  formatValidQueryParamUrlCommaSeparated
} from "../utils/common";
import { emptySplitApi } from "./empty-split-api";
import {
  PatientProduct,
  PatientProductFilters,
  PatientProductInput,
  PatientProductUpdateInput,
  PatientProductsDownloadQuery,
  PatientProductsResponse
} from "./types/patient-products.type";
import { CSVReportResponse } from "./types/reports.type";

export const DEFAULT_PATIENT_PRODUCTS_LIMIT = 25;

export const patientProductsApi = emptySplitApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    getPatientProducts: builder.query<PatientProduct[], PatientProductFilters>({
      query: (params) =>
        getPatientProductsEndpointWithQueryParameters(
          "/patient-products/",
          params
        ),
      transformResponse: (response: PatientProductsResponse) => {
        return response.results;
      },
      providesTags: (patientProducts) => {
        if (
          patientProducts?.find(
            (product) => product.assigned_tags !== undefined
          )
        ) {
          return ["PatientProducts", "PatientProductsWithAssignedTags"];
        }

        return ["PatientProducts"];
      }
    }),
    getPatientProductsUniquePatientsCount: builder.query<
      { count: number; count_sms: number; count_distinct_patients: number },
      PatientProductFilters
    >({
      query: (params) =>
        getPatientProductsEndpointWithQueryParameters(
          "/patient-products/patients-count/",
          {
            ...params,
            limit: 0,
            offset: 0
          }
        ),
      transformResponse: (response: {
        count: number;
        count_sms: number;
        count_distinct_patients: number;
      }) => {
        return {
          count: Number(response.count || 0),
          count_sms: Number(response.count_sms || 0),
          count_distinct_patients: Number(response.count_distinct_patients || 0)
        };
      },
      providesTags: ["PatientProducts"]
    }),
    getPatientProductsCount: builder.query<
      number,
      Omit<PatientProductFilters, "query" | "limit" | "offset">
    >({
      query: ({ baseFilters, ...filters }) =>
        `${
          viteEnv.VITE_SUNO_API
        }/patient-products/count/?${formatValidQueryParamUrlCommaSeparated({
          ...baseFilters,
          ...filters
        })}`,
      providesTags: ["PatientProductsCounters"]
    }),
    getPatientProduct: builder.query<
      PatientProduct,
      {
        patientProductId: number;
        query?: string;
      }
    >({
      query: ({ patientProductId, query = "{*}" }) =>
        `${
          viteEnv.VITE_SUNO_API
        }/patient-products/${patientProductId}/?query=${query}`,
      providesTags: ["PatientProducts"]
    }),
    addPatientProduct: builder.mutation<PatientProduct, PatientProductInput>({
      query: (patientProduct) => ({
        url: `${viteEnv.VITE_SUNO_API}/patient-products/`,
        method: "POST",
        body: patientProduct
      }),
      invalidatesTags: [
        "PatientProducts",
        "MergeTagsPatientProduct",
        "MergeTagsSale",
        "SaleItems"
      ]
    }),
    updatePatientProduct: builder.mutation<
      PatientProduct,
      PatientProductUpdateInput
    >({
      query: ({ id, ...patientProduct }) => ({
        url: `${viteEnv.VITE_SUNO_API}/patient-products/${id}/`,
        method: "PATCH",
        body: patientProduct
      }),
      invalidatesTags: [
        "PatientProducts",
        "MergeTagsPatientProduct",
        "MergeTagsSale",
        "SaleItems"
      ]
    }),
    deletePatientProduct: builder.mutation<void, number>({
      query: (id) => ({
        url: `${viteEnv.VITE_SUNO_API}/patient-products/${id}/`,
        method: "DELETE"
      }),
      invalidatesTags: ["PatientProducts", "MergeTagsSale", "SaleItems"]
    }),
    downloadPatientProductsCSV: builder.query<
      CSVReportResponse,
      PatientProductsDownloadQuery
    >({
      query: ({ query = "{*}", csvHeader = "*", csvLabels, ...filters }) => ({
        url: `${
          viteEnv.VITE_SUNO_API
        }/patient-products/?query=${query}&csv_header=${csvHeader}&${formatValidQueryParamUrl(
          { csv_labels: csvLabels, ...filters }
        )}`,
        headers: { Accept: "custom-csv/json" }
      })
    })
  })
});

export const {
  useGetPatientProductsQuery,
  useGetPatientProductsUniquePatientsCountQuery,
  useAddPatientProductMutation,
  useUpdatePatientProductMutation,
  useGetPatientProductQuery,
  useLazyGetPatientProductsCountQuery,
  useLazyDownloadPatientProductsCSVQuery,
  useDeletePatientProductMutation,
  useLazyGetPatientProductsQuery
} = patientProductsApi;
